/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Download, Loader2, ArrowLeft, FileText } from 'lucide-react';
import { Placement } from '../../types';
import { GRADUATE_ATTRIBUTES, VOCATIONAL_SKILLS } from '../../data/evaluationConstants';
import { useWBL } from '../../context/WBLContext';
import { collection, doc, getDoc, getDocs, query, where, onSnapshot } from 'firebase/firestore';
import { db, isUsingMockKeys } from '../../firebase';

interface TrainingAgreementDocProps {
  placement: Placement;
  onClose: () => void;
  onEditAgreement?: () => void;
}

export const TrainingAgreementDoc: React.FC<TrainingAgreementDocProps> = ({ placement, onClose, onEditAgreement }) => {
  const { users, schools, activeUser } = useWBL();
  const agreement = placement?.agreement;

  const evaluatedSkills = React.useMemo(() => {
    const ids1 = placement?.eval1?.vocationalSkillIds || [];
    const ids2 = placement?.eval2?.vocationalSkillIds || [];
    const union = Array.from(new Set([...ids1, ...ids2]));
    const skillIds = union.length > 0 ? union : ['leadership', 'computer_tech', 'database_usage'];
    return skillIds.map(id => VOCATIONAL_SKILLS.find(s => s.id === id)).filter(Boolean) as typeof VOCATIONAL_SKILLS;
  }, [placement]);

  const [localStudent, setLocalStudent] = useState<any>(null);
  const [localCompany, setLocalCompany] = useState<any>(null);
  const [districts, setDistricts] = useState<{ id: string; name: string }[]>([]);

  // 1. Fetch districts list
  useEffect(() => {
    if (isUsingMockKeys) return;
    const unsub = onSnapshot(collection(db, "districts"), (snap) => {
      const list: { id: string; name: string }[] = [];
      snap.forEach((d) => list.push({ id: d.id, name: d.data().name }));
      setDistricts(list);
    });
    return () => unsub();
  }, []);

  // 2. Load Student profile dynamically
  useEffect(() => {
    const studentFromContext = (users || []).find(u => u.uid === placement.studentId);
    if (studentFromContext) {
      setLocalStudent(studentFromContext);
    } else if (!isUsingMockKeys) {
      getDoc(doc(db, 'users', placement.studentId)).then(snapshot => {
        if (snapshot.exists()) {
          setLocalStudent({ uid: snapshot.id, ...snapshot.data() });
        }
      }).catch(err => console.error("Error fetching student profile:", err));
    }
  }, [users, placement.studentId]);

  // 3. Load Company profile dynamically
  useEffect(() => {
    const companyFromContext = (users || []).find(u => 
      u.role === 'organization' && 
      (u.uid === placement.organizationId || u.orgName === placement.organizationName)
    );
    if (companyFromContext) {
      setLocalCompany(companyFromContext);
    } else if (!isUsingMockKeys) {
      // Fetch by organizationRepresentative uid
      getDoc(doc(db, 'users', placement.organizationId)).then(snapshot => {
        if (snapshot.exists()) {
          setLocalCompany({ uid: snapshot.id, ...snapshot.data() });
        } else if (placement.organizationName) {
          const q = query(collection(db, 'users'), where('role', '==', 'organization'), where('orgName', '==', placement.organizationName));
          getDocs(q).then(snap => {
            if (!snap.empty) {
              setLocalCompany({ uid: snap.docs[0].id, ...snap.docs[0].data() });
            }
          });
        }
      }).catch(err => console.error("Error fetching company profile:", err));
    }
  }, [users, placement.organizationId, placement.organizationName]);

  // Clean suffix (spartanburg) or (training)
  const cleanDistrictName = (districtStr: string) => {
    if (!districtStr) return '';
    return districtStr.replace(/\s*\([^)]*\)/g, '').trim();
  };

  const cleanSchoolName = (schoolStr: string) => {
    if (!schoolStr) return '';
    return schoolStr.replace(/\s*\([^)]*\)/g, '').trim();
  };

  const resolveSchoolName = (schoolRef: string) => {
    if (!schoolRef) return '';
    const found = (schools || []).find(s => s.id === schoolRef || s.name.toLowerCase() === schoolRef.toLowerCase());
    return found ? found.name : schoolRef;
  };

  const resolveDistrictName = (districtRef: string) => {
    if (!districtRef) return '';
    const found = districts.find(d => d.id === districtRef || d.name.toLowerCase() === districtRef.toLowerCase());
    return found ? found.name : districtRef;
  };

  const activeStudent = localStudent || (users || []).find(u => u.uid === placement.studentId);
  const activeCompany = localCompany || (users || []).find(u => 
    u.role === 'organization' && 
    (u.uid === placement.organizationId || u.orgName === placement.organizationName)
  );

  const studentSchool = cleanSchoolName(resolveSchoolName(activeStudent?.school || ''));
  const studentDistrict = cleanDistrictName(resolveDistrictName(activeStudent?.district || ''));

  const worksitePhone = activeCompany?.contactPhone || '';
  const worksiteEmail = activeCompany?.email || '';

  // Student's career path empty unless advisor puts it in
  const careerPathway = agreement?.careerPathway || '';

  // Render Yes/No helper (bold, same color, only circled if answered by user)
  const renderYesNo = (val: boolean | undefined, isYes: boolean) => {
    const hasValue = val !== undefined && val !== null;
    const isSelected = hasValue && val === isYes;
    
    // Bold, same color by default. Circular outline only when selected.
    const baseClass = "font-bold text-slate-800 text-[10px] inline-flex items-center justify-center px-2 py-0.5 rounded-full transition-all";
    const circleClass = isSelected ? "border-2 border-slate-900" : "border-2 border-transparent";
    
    return (
      <span className={`${baseClass} ${circleClass}`}>
        {isYes ? "YES" : "NO"}
      </span>
    );
  };
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    // Ensure we start at the top
    const scrollToTop = () => {
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
    };
    
    // Call immediately and again after a short delay to handle content rendering
    scrollToTop();
    const timer = setTimeout(scrollToTop, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleDownloadPDF = async () => {
    if (!contentRef.current) return;
    setIsDownloading(true);

    try {
      const pdf = new jsPDF('p', 'in', 'letter');
      const pageElements = contentRef.current.querySelectorAll('.pdf-page');

      for (let i = 0; i < pageElements.length; i++) {
        const el = pageElements[i] as HTMLDivElement;

        // Apply clean style overrides sequentially so the html2canvas renderer gets pristine dimensions
        const originalStyle = el.style.cssText;
        el.style.width = '8.5in';
        el.style.height = '11in';
        el.style.boxSizing = 'border-box';
        el.style.boxShadow = 'none';
        el.style.border = 'none';
        el.style.margin = '0';

        const canvas = await html2canvas(el, {
          scale: 2, // High DPI resolution matching original graphics perfectly
          useCORS: true,
          allowTaint: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: el.offsetWidth,
          height: el.offsetHeight
        });

        const imgData = canvas.toDataURL('image/png');

        if (i > 0) {
          pdf.addPage();
        }

        // Draw exactly onto the letter dimensions (8.5 x 11 inches)
        pdf.addImage(imgData, 'PNG', 0, 0, 8.5, 11);

        el.style.cssText = originalStyle;
      }

      pdf.save(`Training_Agreement_${placement.studentName.replace(/\s+/g, '_')}.pdf`);
    } catch (err) {
      console.error("Failed to generate PDF:", err);
    } finally {
      setIsDownloading(false);
    }
  };

  const renderSig = (sig?: string, fallback?: string) => {
    const value = sig || fallback;
    if (!value) return '';
    if (value.startsWith('data:image')) {
      return <img src={value} alt="Signature" className="h-6 object-contain" />;
    }
    return value;
  };

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 bg-slate-900/65 backdrop-blur-sm z-[100] flex items-start justify-center p-4 overflow-y-auto print:static print:bg-white print:p-0"
    >
      <div className="w-full max-w-[9in] relative print:w-full min-h-screen">
        {/* Close Button / Toolbar */}
        <div className="sticky top-0 bg-slate-100 p-4 border-b border-slate-200 flex items-center justify-between z-10 print:hidden rounded-lg shadow-md mb-4 max-w-[8.5in] mx-auto">
          <div className="flex items-center space-x-4">
             <button onClick={onClose} className="text-slate-600 font-black uppercase text-[10px] tracking-widest hover:text-slate-900 flex items-center">
                <ArrowLeft className="w-3 h-3 mr-2" /> Return
             </button>
             <h2 className="text-xs font-black uppercase tracking-widest text-slate-400">Document Preview</h2>
          </div>
          <div className="flex items-center space-x-2">
            {onEditAgreement && activeUser?.role !== 'student' && activeUser?.role !== 'parent' && (
              <button
                onClick={() => {
                  onClose();
                  onEditAgreement();
                }}
                className="bg-[#0094c8] text-white px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[#007ba6] transition-all flex items-center shadow-xs"
              >
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                <span>Manage Training Agreement</span>
              </button>
            )}
            <button 
              onClick={handleDownloadPDF} 
              disabled={isDownloading}
              className="bg-slate-900 text-white px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center disabled:opacity-50"
            >
              {isDownloading ? (
                <>
                  <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-3 h-3 mr-2" />
                  Download Agreement
                </>
              )}
            </button>
          </div>
        </div>

        <div ref={contentRef} className="flex flex-col gap-6 items-center">
          <style dangerouslySetInnerHTML={{ __html: `
            .bg-slate-50 { background-color: #f8fafc !important; }
            .bg-slate-100 { background-color: #f1f5f9 !important; }
            .bg-slate-900 { background-color: #0f172a !important; }
            .text-slate-800 { color: #1e293b !important; }
            .text-slate-700 { color: #334155 !important; }
            .text-slate-600 { color: #475569 !important; }
            .text-slate-500 { color: #64748b !important; }
            .text-slate-400 { color: #94a3b8 !important; }
            .text-slate-300 { color: #cbd5e1 !important; }
            .border-slate-300 { border-color: #cbd5e1 !important; }
            .border-slate-200 { border-color: #e2e8f0 !important; }
            .border-slate-100 { border-color: #f1f5f9 !important; }
            .border-slate-900 { border-color: #0f172a !important; }
            .text-blue-900 { color: #1e3a8a !important; }
            .bg-blue-50\\/20 { background-color: #f0f7ff !important; }
            .bg-emerald-50\\/20 { background-color: #f0fdf4 !important; }

            .pdf-page {
              width: 8.5in !important;
              height: 11in !important;
              box-sizing: border-box !important;
              background-color: #ffffff !important;
              position: relative !important;
            }
          `}} />

          {/* PAGE 1 */}
          <div className="pdf-page p-[0.5in] md:p-[0.75in] bg-white font-sans text-slate-800 flex flex-col justify-between shadow-xl" style={{ boxSizing: 'border-box' }}>
            <div>
              <div className="border-t-[3px] border-b border-slate-300 pt-1 pb-1 mb-4 text-center">
                <h1 className="text-sm font-bold uppercase tracking-tight text-slate-700">South Carolina Work-Based Learning Training Agreement/Evaluation Plan</h1>
              </div>

              <div className="space-y-1 text-xs">
                <div className="flex border-b border-slate-100 py-1">
                  <span className="font-bold w-1/3 text-slate-500">Date:</span>
                  <span className="font-bold">{new Date().toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' })}</span>
                </div>
                <div className="flex border-b border-slate-100 py-1">
                  <span className="font-bold w-1/3 text-slate-500">Student's Name:</span>
                  <span className="font-bold text-blue-900">{placement.studentName}</span>
                </div>
                <div className="flex border-b border-slate-100 py-1">
                  <span className="font-bold w-1/3 text-slate-500">School/Career Center:</span>
                  <span className="font-bold">{studentSchool || 'Bayside High School'}</span>
                </div>
                <div className="flex border-b border-slate-100 py-1">
                  <span className="font-bold w-1/3 text-slate-500">School District:</span>
                  <span className="font-bold">{studentDistrict || 'Training School District'}</span>
                </div>
                <div className="flex border-b border-slate-100 py-1 gap-8">
                  <div className="flex-1 flex">
                    <span className="font-bold w-1/2 text-slate-500">Start Date:</span>
                    <span className="font-bold">{placement.startDate}</span>
                  </div>
                  <div className="flex-1 flex">
                    <span className="font-bold w-1/2 text-slate-500">End Date:</span>
                    <span className="font-bold">{placement.endDate}</span>
                  </div>
                </div>
                 <div className="flex border-b border-slate-100 py-1">
                  <span className="font-bold w-1/3 text-slate-500">Worksite Name:</span>
                  <span className="font-bold">{agreement?.worksiteName || placement.organizationName}</span>
                </div>
                <div className="flex border-b border-slate-100 py-1">
                  <span className="font-bold w-1/3 text-slate-500">Worksite Assigned Days:</span>
                  <span className="font-bold">{(agreement?.assignedDays || placement.assignedDays || []).join(', ')}</span>
                </div>
                <div className="flex border-b border-slate-100 py-1">
                  <span className="font-bold w-1/3 text-slate-500">Worksite Assigned Hours:</span>
                  <span className="font-bold">{agreement?.assignedHoursTotal || placement.assignedHoursTotal || 120} hrs</span>
                </div>
                <div className="flex border-b border-slate-100 py-1">
                  <span className="font-bold w-1/3 text-slate-500">Worksite Supervisor:</span>
                  <span className="font-bold">{agreement?.supervisorName || placement.mentorName}</span>
                </div>
                <div className="flex border-b border-slate-100 py-1">
                  <span className="font-bold w-1/3 text-slate-500">Worksite Phone:</span>
                  <span className="font-bold">{agreement?.supervisorPhone || worksitePhone || placement.directLine || '8646306571'}</span>
                </div>
                <div className="flex border-b border-slate-100 py-1">
                  <span className="font-bold w-1/3 text-slate-500">Worksite Email:</span>
                  <span className="font-bold">{agreement?.supervisorEmail || worksiteEmail || placement.registryContact || 'organization@wblportal.com'}</span>
                </div>
                <div className="flex border-b border-slate-100 py-1">
                  <span className="font-bold w-1/3 text-slate-500">Student's Career Pathway:</span>
                  <span className="font-bold">{careerPathway}</span>
                </div>
              </div>

              <div className="space-y-1 mt-4 text-[9px]">
                {onEditAgreement && (
                  <div className="flex items-center justify-between pb-1 mb-1 border-b border-slate-200 print:hidden">
                    <span className="text-[9px] font-black uppercase text-slate-400 font-mono">WBL Compliance Questions</span>
                    <button
                      onClick={() => {
                        onClose();
                        onEditAgreement();
                      }}
                      className="text-[9px] font-black uppercase text-[#0094c8] hover:underline flex items-center"
                    >
                      <FileText className="w-3 h-3 mr-1" /> Answer Questions
                    </button>
                  </div>
                )}
                <div className="flex justify-between items-center py-1 border-b border-slate-100">
                  <span className="text-slate-600">Is the Work-Based Learning (WBL) Placement aligned to student's Individual Graduation Plan (IGP)?</span>
                  <span className="font-bold space-x-1.5 self-center inline-flex items-center">
                    {renderYesNo(agreement?.isAlignedToIGP, true)}
                    <span className="text-slate-300">/</span>
                    {renderYesNo(agreement?.isAlignedToIGP, false)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-slate-100">
                  <span className="text-slate-600">Is the Work-Based Learning Placement paid?</span>
                  <span className="font-bold space-x-1.5 self-center inline-flex items-center">
                    {renderYesNo(agreement?.isPaid, true)}
                    <span className="text-slate-300">/</span>
                    {renderYesNo(agreement?.isPaid, false)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-slate-100">
                  <span className="text-slate-600">Did the student earn at least one unit of course credit in the career pathway related to the WBL placement?</span>
                  <span className="font-bold space-x-1.5 self-center inline-flex items-center">
                    {renderYesNo(agreement?.earnedCourseCredit, true)}
                    <span className="text-slate-300">/</span>
                    {renderYesNo(agreement?.earnedCourseCredit, false)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-slate-100">
                  <span className="text-slate-600">If yes, what is the course code?</span>
                  <span className="font-bold">{agreement?.courseCode || 'N/A'}</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-slate-600">Is School Insurance coverage provided?</span>
                  <span className="font-bold space-x-1.5 self-center inline-flex items-center">
                    {renderYesNo(agreement?.hasInsurance, true)}
                    <span className="text-slate-300">/</span>
                    {renderYesNo(agreement?.hasInsurance, false)}
                  </span>
                </div>
              </div>

              <div className="space-y-1 mt-4">
                <h3 className="text-[10px] font-bold uppercase tracking-wide border-b-2 border-slate-900 pb-0.5 text-slate-900">Job Description</h3>
                <div className="p-2.5 bg-slate-50 text-[9px] italic border border-slate-200 rounded leading-relaxed min-h-[1.1in] whitespace-pre-wrap">
                  {agreement?.jobDescription || "Provide detailed job tasks and expectations..."}
                </div>
              </div>

              <div className="space-y-1 mt-3">
                <h3 className="text-[10px] font-bold uppercase tracking-wide border-b-2 border-slate-900 pb-0.5 text-slate-900">Learning Objectives & Focus Skills</h3>
                <div className="p-2 bg-white border border-slate-200 rounded text-[9px] space-y-0.5">
                  {(agreement?.learningObjectives || ['1. Professionalism', '2. Communication', '3. Technical Competency']).map((obj, i) => (
                    <p key={i}>{obj}</p>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <div className="border border-slate-300 p-2.5 rounded flex items-start gap-3 mt-4">
                <div className="w-8 h-8 border-2 border-slate-900 flex-shrink-0 flex items-center justify-center font-bold text-base text-slate-900">1</div>
                <div className="text-[8px] leading-relaxed text-slate-600 flex-1">
                  <div className="font-bold text-slate-900 uppercase tracking-wide text-[9px] mb-0.5">Employability Attributes</div>
                  The employability attributes provided below as Profile Elements are essential in every work environment throughout one's career. Discuss and review with the student as each should be viewed as the foundation upon which specific workplace and career ready skills are aligned with the Profile of the SC Graduate.
                </div>
              </div>

              <div className="pt-2 border-t border-slate-100 flex justify-between items-center text-[7px] font-mono text-slate-400 mt-2">
                <span>Region: Training Environment</span>
                <span>Page 1 of 5</span>
              </div>
            </div>
          </div>

          {/* PAGE 2 */}
          <div className="pdf-page p-[0.5in] md:p-[0.75in] bg-white font-sans text-slate-800 flex flex-col justify-between shadow-xl" style={{ boxSizing: 'border-box' }}>
            <div>
              <div className="border-t-[3px] border-b border-slate-300 pt-1 pb-1 mb-4 text-center">
                <h1 className="text-sm font-bold uppercase tracking-tight text-slate-700">South Carolina Work-Based Learning Training Agreement/Evaluation Plan</h1>
              </div>

              <div className="bg-slate-50 p-2 border border-slate-300 rounded text-[7px] space-y-0.5 mb-4 leading-normal">
                <p className="font-bold text-slate-900 uppercase">KEY: Evaluation Score</p>
                <p><span className="font-bold">1 = Performance Improvement Needed:</span> Needs to have a strategy to improve this skill; aware of next steps</p>
                <p><span className="font-bold">2 = Developing:</span> Developing this skill; learning to address challenges related to this skill; aware of next steps</p>
                <p><span className="font-bold">3 = Competent:</span> Demonstrates this skill; aware of the importance of this skill</p>
                <p><span className="font-bold">4 = Proficient:</span> Consistently demonstrates this skill; shows initiative to learn about, enhance or apply this skill</p>
                <p><span className="font-bold">5 = Advanced:</span> Exceeds expectations; works with high level of independence, acts as a role model, or shows initiative to apply and extend this skill</p>
              </div>

              <table className="w-full border-collapse border border-slate-300 text-[8px] leading-tight">
                <thead>
                  <tr className="bg-slate-100 uppercase text-slate-700 font-bold border-b border-slate-300">
                    <th className="border border-slate-300 p-1.5 w-[15%] text-left">PROFILE ELEMENT</th>
                    <th className="border border-slate-300 p-1.5 w-[30%] text-left">PERFORMANCE EXPECTATIONS</th>
                    <th className="border border-slate-300 p-1.5 w-[10%] text-center">REVIEWS<br/>1-5 Scale</th>
                    <th className="border border-slate-300 p-1.5 w-[45%] text-left">COMMENTS<br/><span className="text-[7px] font-medium text-slate-500">Notes, goals, and reflections for Review #1 and Review #2</span></th>
                  </tr>
                </thead>
                <tbody>
                  {GRADUATE_ATTRIBUTES.slice(0, 4).map((attr) => (
                    <React.Fragment key={attr.id}>
                      <tr className="align-top border-t border-slate-300">
                        <td rowSpan={2} className="border border-slate-300 p-1.5 font-bold text-slate-900 bg-slate-50/10">{attr.label}</td>
                        <td rowSpan={2} className="border border-slate-300 p-1.5 text-slate-700">
                          <ul className="list-disc pl-3.5 space-y-0.5">
                            {attr.expectations.map((exp, i) => <li key={i}>{exp}</li>)}
                          </ul>
                        </td>
                        <td className="border border-slate-300 p-1 bg-blue-50/20">
                          <div className="flex items-center justify-between px-1">
                            <span className="text-[7px] text-slate-400 font-bold uppercase">1</span>
                            <span className="text-[11px] font-black text-blue-900">{placement.eval1?.scores[attr.id]?.score || ''}</span>
                          </div>
                        </td>
                        <td className="border border-slate-300 p-1.5 italic text-slate-600 text-[8px]">
                          {placement.eval1?.scores[attr.id]?.comments || <span className="text-slate-300">Awaiting review...</span>}
                        </td>
                      </tr>
                      <tr className="align-top border-b border-slate-300">
                        <td className="border border-slate-300 p-1 bg-emerald-50/20">
                          <div className="flex items-center justify-between px-1">
                            <span className="text-[7px] text-slate-400 font-bold uppercase">2</span>
                            <span className="text-[11px] font-black text-emerald-900">{placement.eval2?.scores[attr.id]?.score || ''}</span>
                          </div>
                        </td>
                        <td className="border border-slate-300 p-1.5 italic text-slate-600 text-[8px]">
                          {placement.eval2?.scores[attr.id]?.comments || <span className="text-slate-300">Awaiting final review...</span>}
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pt-2 border-t border-slate-100 flex justify-between items-center text-[7px] font-mono text-slate-400">
              <span>Region: Training Environment</span>
              <span>Page 2 of 5</span>
            </div>
          </div>

          {/* PAGE 3 */}
          <div className="pdf-page p-[0.5in] md:p-[0.75in] bg-white font-sans text-slate-800 flex flex-col justify-between shadow-xl" style={{ boxSizing: 'border-box' }}>
            <div>
              <div className="border-t-[3px] border-b border-slate-300 pt-1 pb-1 mb-3 text-center">
                <h1 className="text-sm font-bold uppercase tracking-tight text-slate-700">South Carolina Work-Based Learning Training Agreement/Evaluation Plan</h1>
              </div>

              <table className="w-full border-collapse border border-slate-300 text-[7.5px] leading-tight">
                <thead>
                  <tr className="bg-slate-100 uppercase text-slate-700 font-bold border-b border-slate-300">
                    <th className="border border-slate-300 p-1 w-[15%] text-left">PROFILE ELEMENT</th>
                    <th className="border border-slate-300 p-1 w-[30%] text-left">PERFORMANCE EXPECTATIONS</th>
                    <th className="border border-slate-300 p-1 w-[10%] text-center">REVIEWS<br/>1-5 Scale</th>
                    <th className="border border-slate-300 p-1 w-[45%] text-left">COMMENTS<br/><span className="text-[6.5px] font-medium text-slate-500">Notes, goals, and reflections for Review #1 and Review #2</span></th>
                  </tr>
                </thead>
                <tbody>
                  {GRADUATE_ATTRIBUTES.slice(4, 10).map((attr) => (
                    <React.Fragment key={attr.id}>
                      <tr className="align-top border-t border-slate-200">
                        <td rowSpan={2} className="border border-slate-300 p-1 font-bold text-slate-900 bg-slate-50/10 leading-snug">{attr.label}</td>
                        <td rowSpan={2} className="border border-slate-300 p-1 text-slate-700">
                          <ul className="list-disc pl-3 space-y-0.5">
                            {attr.expectations.map((exp, i) => <li key={i}>{exp}</li>)}
                          </ul>
                        </td>
                        <td className="border border-slate-300 p-0.5 bg-blue-50/20">
                          <div className="flex items-center justify-between px-0.5">
                            <span className="text-[6.5px] text-slate-400 font-bold">1</span>
                            <span className="text-[10px] font-black text-blue-900">{placement.eval1?.scores[attr.id]?.score || ''}</span>
                          </div>
                        </td>
                        <td className="border border-slate-300 p-1 italic text-slate-600 text-[7.5px] leading-normal whitespace-normal break-words">
                          {placement.eval1?.scores[attr.id]?.comments || <span className="text-slate-300">Awaiting review...</span>}
                        </td>
                      </tr>
                      <tr className="align-top border-b border-slate-200">
                        <td className="border border-slate-300 p-0.5 bg-emerald-50/20">
                          <div className="flex items-center justify-between px-0.5">
                            <span className="text-[6.5px] text-slate-400 font-bold">2</span>
                            <span className="text-[10px] font-black text-emerald-900">{placement.eval2?.scores[attr.id]?.score || ''}</span>
                          </div>
                        </td>
                        <td className="border border-slate-300 p-1 italic text-slate-600 text-[7.5px] leading-normal whitespace-normal break-words">
                          {placement.eval2?.scores[attr.id]?.comments || <span className="text-slate-300">Awaiting final review...</span>}
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pt-1.5 border-t border-slate-100 flex justify-between items-center text-[7px] font-mono text-slate-400 mt-2">
              <span>Region: Training Environment</span>
              <span>Page 3 of 5</span>
            </div>
          </div>

          {/* PAGE 4 */}
          <div className="pdf-page p-[0.5in] md:p-[0.75in] bg-white font-sans text-slate-800 flex flex-col justify-between shadow-xl" style={{ boxSizing: 'border-box' }}>
            <div>
              <div className="border-t-[3px] border-b border-slate-300 pt-1 pb-1 mb-3 text-center">
                <h1 className="text-sm font-bold uppercase tracking-tight text-slate-700">South Carolina Work-Based Learning Training Agreement/Evaluation Plan</h1>
              </div>

              <div className="space-y-1 mt-1 mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 border border-slate-900 flex-shrink-0 flex items-center justify-center font-bold text-sm text-slate-900">2</div>
                  <h3 className="text-[10px] font-bold uppercase text-slate-900">Workplace & Career Specific Skills</h3>
                </div>
                <p className="text-[8px] text-slate-500 leading-tight">List three to five skills that will be a focus for this work-based learning experience. Choose from the following examples or identify other skills relevant to the specific worksite environment and/or career goals.</p>
              </div>

              {/* Taxonomy Grid */}
              <div className="grid grid-cols-4 border border-slate-300 text-[6.5px] gap-0 text-slate-600 mb-4 bg-slate-50/55 rounded overflow-hidden">
                <div className="p-1.5 border-r border-slate-300">
                  <div className="font-bold text-slate-800 uppercase pb-1 border-b border-slate-200 mb-1 text-[7px]">Career/Engagement</div>
                  <ul className="space-y-0.5">
                    <li>• Adaptability</li>
                    <li>• Collecting & Organizing</li>
                    <li>• Creativity</li>
                    <li>• Professionalism</li>
                    <li>• Leadership</li>
                    <li>• Project Management</li>
                    <li>• Public Speaking</li>
                    <li>• Understanding Aspects</li>
                  </ul>
                </div>
                <div className="p-1.5 border-r border-slate-300">
                  <div className="font-bold text-slate-800 uppercase pb-1 border-b border-slate-200 mb-1 text-[7px]">Digital Literacy</div>
                  <ul className="space-y-0.5">
                    <li>• Computer Technology</li>
                    <li>• Database Usage</li>
                    <li>• Graphic Design</li>
                    <li>• Media Literacy</li>
                    <li>• Office Suite Software</li>
                    <li>• Coding</li>
                    <li>• Software Development</li>
                    <li>• Spreadsheet Usage</li>
                    <li>• Web Development</li>
                  </ul>
                </div>
                <div className="p-1.5 border-r border-slate-300">
                  <div className="font-bold text-slate-800 uppercase pb-1 border-b border-slate-200 mb-1 text-[7px]">Applied Academic</div>
                  <ul className="space-y-0.5">
                    <li>• Applied Mathematics</li>
                    <li>• Reading</li>
                    <li>• Research and Analysis</li>
                    <li>• Writing</li>
                    <li>• Multiple Languages</li>
                  </ul>
                </div>
                <div className="p-1.5">
                  <div className="font-bold text-slate-800 uppercase pb-1 border-b border-slate-200 mb-1 text-[7px]">STEM-Related</div>
                  <ul className="space-y-0.5">
                    <li>• Analyze</li>
                    <li>• Engineering Concepts</li>
                    <li>• Technology Driven</li>
                    <li>• Science Concepts</li>
                    <li>• Cause/Effect relations</li>
                    <li>• Predict/Conclude</li>
                    <li>• Repair equipment</li>
                    <li>• Uses software</li>
                    <li>• Mathematics concepts</li>
                  </ul>
                </div>
              </div>

              {/* Evaluation Table of Selected Workplace & Career Skills */}
              <table className="w-full border-collapse border border-slate-300 text-[8px] leading-tight mt-2">
                <thead>
                  <tr className="bg-slate-100 uppercase text-slate-700 font-bold border-b border-slate-300">
                    <th className="border border-slate-300 p-1.5 w-[15%] text-left">SKILL</th>
                    <th className="border border-slate-300 p-1.5 w-[30%] text-left">SKILL DEFINITION</th>
                    <th className="border border-slate-300 p-1.5 w-[10%] text-center">REVIEWS<br/>1-5 Scale</th>
                    <th className="border border-slate-300 p-1.5 w-[45%] text-left">COMMENTS<br/><span className="text-[7px] font-medium text-slate-500">Notes, goals, and reflections for Review #1 and Review #2</span></th>
                  </tr>
                </thead>
                <tbody>
                  {evaluatedSkills.map((skill) => (
                    <React.Fragment key={skill.id}>
                      <tr className="align-top border-t border-slate-300">
                        <td rowSpan={2} className="border border-slate-300 p-1.5 font-bold text-slate-900 bg-slate-50/10">{skill.label}</td>
                        <td rowSpan={2} className="border border-slate-300 p-1.5 text-slate-700 text-[7.5px] leading-relaxed">
                          {skill.definition}
                        </td>
                        <td className="border border-slate-300 p-1 bg-blue-50/20">
                          <div className="flex items-center justify-between px-1">
                            <span className="text-[7px] text-slate-400 font-bold uppercase">1</span>
                            <span className="text-[11px] font-black text-blue-900">{placement.eval1?.scores[skill.id]?.score || ''}</span>
                          </div>
                        </td>
                        <td className="border border-slate-300 p-1.5 italic text-slate-600 text-[8px]">
                          {placement.eval1?.scores[skill.id]?.comments || <span className="text-slate-300">Awaiting review...</span>}
                        </td>
                      </tr>
                      <tr className="align-top border-b border-slate-300">
                        <td className="border border-slate-300 p-1 bg-emerald-50/20">
                          <div className="flex items-center justify-between px-1">
                            <span className="text-[7px] text-slate-400 font-bold uppercase">2</span>
                            <span className="text-[11px] font-black text-emerald-900">{placement.eval2?.scores[skill.id]?.score || ''}</span>
                          </div>
                        </td>
                        <td className="border border-slate-300 p-1.5 italic text-slate-600 text-[8px]">
                          {placement.eval2?.scores[skill.id]?.comments || <span className="text-slate-300">Awaiting final review...</span>}
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pt-2 border-t border-slate-100 flex justify-between items-center text-[7px] font-mono text-slate-400">
              <span>Region: Training Environment</span>
              <span>Page 4 of 5</span>
            </div>
          </div>

          {/* PAGE 5 */}
          <div className="pdf-page p-[0.5in] md:p-[0.75in] bg-white font-sans text-slate-800 flex flex-col justify-between shadow-xl" style={{ boxSizing: 'border-box' }}>
            <div>
              <div className="border-t-[3px] border-b border-slate-300 pt-1 pb-1 mb-8 text-center">
                <h1 className="text-sm font-bold uppercase tracking-tight text-slate-700">South Carolina Work-Based Learning Training Agreement/Evaluation Plan</h1>
              </div>

              <div className="grid grid-cols-2 gap-10 mt-6">
                <div className="space-y-6">
                  <h3 className="bg-slate-100 px-3 py-1.5 font-black text-xs uppercase tracking-wider inline-block border-l-4 border-slate-900">EVALUATION REVIEW 1:</h3>
                  
                  <div className="space-y-5 text-[9px] mt-2">
                    <div className="border-b border-slate-300 pb-1 flex justify-between h-8 items-end">
                      <span className="text-slate-400 uppercase font-mono text-[8px]">Student Signature:</span>
                      <span className="font-medium font-serif italic text-blue-900">{renderSig(placement.eval1?.studentSignature, placement.eval1?.status === 'released' ? placement.studentName : '')}</span>
                    </div>
                    <div className="border-b border-slate-300 pb-1 flex justify-between h-8 items-end">
                      <span className="text-slate-400 uppercase font-mono text-[8px]">Worksite Supervisor Signature:</span>
                      <span className="font-medium font-serif italic text-blue-900">{renderSig(placement.eval1?.supervisorSignature, placement.eval1?.status === 'released' ? placement.mentorName : '')}</span>
                    </div>
                    <div className="border-b border-slate-300 pb-1 flex justify-between h-8 items-end">
                      <span className="text-slate-400 uppercase font-mono text-[8px]">School Advisor Signature:</span>
                      <span className="font-medium font-serif italic text-blue-900">{renderSig(placement.eval1?.advisorSignature)}</span>
                    </div>
                    <div className="border-b border-slate-300 pb-1 flex justify-between items-end">
                      <span className="text-slate-400 uppercase font-mono text-[8px]">Date:</span>
                      <span className="font-medium">{placement.eval1?.submittedAt?.split('T')[0] || (placement.eval1?.status === 'released' ? '05/19/2026' : '___/___/___')}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <h3 className="bg-slate-100 px-3 py-1.5 font-black text-xs uppercase tracking-wider inline-block border-l-4 border-slate-900">EVALUATION REVIEW 2:</h3>
                  
                  <div className="space-y-5 text-[9px] mt-2">
                    <div className="border-b border-slate-300 pb-1 flex justify-between h-8 items-end">
                      <span className="text-slate-400 uppercase font-mono text-[8px]">Student Signature:</span>
                      <span className="font-medium font-serif italic text-blue-900">{renderSig(placement.eval2?.studentSignature, placement.eval2?.status === 'released' ? placement.studentName : '')}</span>
                    </div>
                    <div className="border-b border-slate-300 pb-1 flex justify-between h-8 items-end">
                      <span className="text-slate-400 uppercase font-mono text-[8px]">Worksite Supervisor Signature:</span>
                      <span className="font-medium font-serif italic text-blue-900">{renderSig(placement.eval2?.supervisorSignature, placement.eval2?.status === 'released' ? placement.mentorName : '')}</span>
                    </div>
                    <div className="border-b border-slate-300 pb-1 flex justify-between h-8 items-end">
                      <span className="text-slate-400 uppercase font-mono text-[8px]">School Advisor Signature:</span>
                      <span className="font-medium font-serif italic text-blue-900">{renderSig(placement.eval2?.advisorSignature)}</span>
                    </div>
                    <div className="border-b border-slate-300 pb-1 flex justify-between items-end">
                      <span className="text-slate-400 uppercase font-mono text-[8px]">Date:</span>
                      <span className="font-medium">{placement.eval2?.submittedAt?.split('T')[0] || (placement.eval2?.status === 'released' ? '05/19/2026' : '___/___/___')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100 flex justify-between items-center text-[7px] font-mono text-slate-400">
              <span>Region: Training Environment</span>
              <span>Page 5 of 5</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};
